export interface HealthCheckResult {
  isHealthy: boolean;
  timestamp: number;
  responseTime: number;
  error?: string;
}

class HealthCheckService {
  private lastCheck: number = 0;
  private cachedResult: HealthCheckResult | null = null;
  private readonly CACHE_DURATION = 30000; // 30 Sekunden Cache
  private readonly TIMEOUT_DURATION = 2000; // 2 Sekunden Timeout

  async isHealthy(): Promise<boolean> {
    const result = await this.getHealthStatus();
    return result.isHealthy;
  }

  async getHealthStatus(): Promise<HealthCheckResult> {
    const now = Date.now();
    
    // Return cached result if still valid
    if (this.cachedResult && (now - this.lastCheck < this.CACHE_DURATION)) {
      console.log('Health check: Using cached result', this.cachedResult);
      return this.cachedResult;
    }

    console.log('Health check: Performing new check...');
    const startTime = Date.now();

    try {
      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT_DURATION);

      const response = await fetch('/api/health', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;

      const result: HealthCheckResult = {
        isHealthy: response.ok,
        timestamp: now,
        responseTime,
        error: response.ok ? undefined : `HTTP ${response.status}: ${response.statusText}`
      };

      // Cache the result
      this.cachedResult = result;
      this.lastCheck = now;

      console.log('Health check completed:', result);
      return result;

    } catch (error) {
      const responseTime = Date.now() - startTime;
      const result: HealthCheckResult = {
        isHealthy: false,
        timestamp: now,
        responseTime,
        error: error instanceof Error ? error.message : 'Unknown health check error'
      };

      // Cache negative results for shorter duration
      this.cachedResult = result;
      this.lastCheck = now - (this.CACHE_DURATION * 0.7); // Cache negative results for 70% of normal duration

      console.error('Health check failed:', result);
      return result;
    }
  }

  // Force refresh of health status
  async forceRefresh(): Promise<HealthCheckResult> {
    this.lastCheck = 0;
    this.cachedResult = null;
    return this.getHealthStatus();
  }

  // Get cached status without making new request
  getCachedStatus(): HealthCheckResult | null {
    const now = Date.now();
    if (this.cachedResult && (now - this.lastCheck < this.CACHE_DURATION)) {
      return this.cachedResult;
    }
    return null;
  }

  // Clear cache
  clearCache(): void {
    this.lastCheck = 0;
    this.cachedResult = null;
  }
}

// Export singleton instance
export const healthCheckService = new HealthCheckService(); 